import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SerieLayer, SerieLayerStateItem } from '@/api/generated/schemas';
import { LayerState } from '@/api/generated/schemas';
import {
  Button,
  MenuItem,
  MenuLabel,
  SplitButton,
  type ButtonSize,
  type ButtonVariant,
} from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import { buildApproveLayerPlan, layerActions } from './approveLayerPlan';

/** Tlačítko změny stavu nese solid barvu svého cílového stavu (stejný tón jako stavový badge). */
const TARGET_VARIANT: Record<LayerState, ButtonVariant> = {
  [LayerState.Locked]: 'default',
  [LayerState.Draft]: 'draft',
  [LayerState.Prepared]: 'info',
  [LayerState.Approved]: 'success',
};

export interface ApproveLayerButtonProps {
  /** Vrstva, které se akce týkají (odpovídá aktivní záložce / panelu). */
  currentLayer: SerieLayer;
  /** Vrstvy ovládané entity (termínu, nebo seriálu). */
  layers: SerieLayerStateItem[];
  /**
   * Vrstvy nadřazeného seriálu – zdroj alternativ „schválit až po X“. Ve scope seriálu vynech,
   * tlačítko se pak chová jako obyčejný Button bez šipky.
   */
  serieLayers?: SerieLayerStateItem[];
  /**
   * Uloží rozpracované změny před akcí. Když vrátí `false`, akce se přeruší
   * (schvalovat/připravovat neuložený obsah nedává smysl).
   */
  onBeforeAction?: () => boolean | Promise<boolean>;
  /** Provede změnu stavu vrstvy na `targetState` (pro schválení je `layer` cílová vrstva řetězu). */
  onChangeState: (layer: SerieLayer, targetState: LayerState) => Promise<unknown>;
  /** Externí důvod pro zablokování (rozpracovaný formulář, read-only board…). */
  disabled?: boolean;
  /** Běží ukládání nebo změna stavu. */
  loading?: boolean;
  size?: ButtonSize;
  /** V patičce formuláře `top`, v panelu sidebaru `bottom`. */
  menuPlacement?: 'top' | 'bottom';
  className?: string;
  'data-testid'?: string;
}

/**
 * Akce nad stavem vrstvy – jednotné pro detail seriálu, detail termínu, boardy i sidebar kalendáře.
 *
 * Podle stavu vrstvy nabídne: „Připravit“ (Draft→Prepared), „Schválit“ (+ „až po vrstvu X“),
 * „Vrátit do rozpracování“ (Prepared→Draft) a „Vrátit schválení“ (na poslední schválené vrstvě).
 * Uložení je implicitní. Vše volá jednu stavově-změnovou mutaci `onChangeState`.
 */
export function ApproveLayerButton({
  currentLayer,
  layers,
  serieLayers,
  onBeforeAction,
  onChangeState,
  disabled,
  loading,
  size = 'lg',
  menuPlacement = 'top',
  className,
  'data-testid': dataTestId,
}: ApproveLayerButtonProps) {
  const { t } = useTranslation('venua-planner');

  const plan = useMemo(
    () => buildApproveLayerPlan({ layers, serieLayers, currentLayer }),
    [layers, serieLayers, currentLayer]
  );

  const actions = useMemo(() => {
    const state = layers.find((entry) => entry.layer === currentLayer)?.state;
    if (state === undefined) return null;
    // Scope seriálu = bez `serieLayers` (nad seriálem už není strop); scope termínu = `serieLayers` předané.
    return layerActions(currentLayer, state, layers, serieLayers === undefined);
  }, [layers, currentLayer, serieLayers]);

  const run = async (layer: SerieLayer, targetState: LayerState) => {
    if (onBeforeAction && !(await onBeforeAction())) return;
    await onChangeState(layer, targetState);
  };

  if (!actions) return null;

  return (
    <div className={cn('gap-phx-2 inline-flex', className)}>
      {actions.canReturnToDraft ? (
        <Button
          variant={TARGET_VARIANT[LayerState.Draft]}
          size={size}
          iconLeft="undo"
          disabled={disabled}
          loading={loading}
          onClick={() => void run(currentLayer, LayerState.Draft)}
        >
          {t('prepare.revert')}
        </Button>
      ) : null}

      {actions.canPrepare ? (
        <Button
          variant={TARGET_VARIANT[LayerState.Prepared]}
          size={size}
          iconLeft="ok"
          disabled={disabled}
          loading={loading}
          onClick={() => void run(currentLayer, LayerState.Prepared)}
        >
          {t('prepare.action')}
        </Button>
      ) : null}

      {actions.canApprove && plan.primary ? (
        <SplitButton
          variant={TARGET_VARIANT[LayerState.Approved]}
          size={size}
          iconLeft="ok"
          data-testid={dataTestId}
          loading={loading}
          disabled={disabled}
          menuPlacement={menuPlacement}
          menuAriaLabel={t('approve.menuAria')}
          onClick={() => void run(currentLayer, LayerState.Approved)}
          menu={
            plan.through.length > 0 ? (
              <>
                <MenuLabel>{t('approve.menuLabel')}</MenuLabel>
                {plan.through.map((option) => (
                  <MenuItem
                    key={option.layer}
                    icon="ok"
                    onClick={() => void run(option.layer, LayerState.Approved)}
                  >
                    {t('approve.through', { layer: t(`serieDetail.layers.${option.layerKey}`) })}
                  </MenuItem>
                ))}
              </>
            ) : undefined
          }
        >
          {t('approve.action')}
        </SplitButton>
      ) : null}

      {actions.canUnapprove ? (
        <Button
          variant={TARGET_VARIANT[LayerState.Draft]}
          size={size}
          iconLeft="undo"
          disabled={disabled}
          loading={loading}
          onClick={() => void run(currentLayer, LayerState.Draft)}
        >
          {/* Vracení schválení je vždy do Draftu → barva i popisek jako u „Vrátit do přípravy“. */}
          {t('prepare.revert')}
        </Button>
      ) : null}
    </div>
  );
}
